import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface LogEntry {
  id: number;
  level: string;
  request_id: string;
  message: string;
  metadata: string;
  created_at: string;
}

interface TTSEntry {
  language: string;
  total: number;
  failures: number;
  rate: string;
}

interface TTSProviderEntry {
  provider: string;
  total: number;
  failures: number;
  rate: string;
}

export default function Logs() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('');
  const [requestFilter, setRequestFilter] = useState('');
  const [page, setPage] = useState(0);
  const [ttsData, setTtsData] = useState<{ byLanguage: TTSEntry[]; byProvider: TTSProviderEntry[]; recentFailures: number } | null>(null);
  const [showTTS, setShowTTS] = useState(false);

  const loadLogs = useCallback(() => {
    setLoading(true);
    api.getAppLogs({ level: levelFilter || undefined, requestId: requestFilter || undefined, limit: 50, offset: page * 50 })
      .then(data => { setLogs(data.logs); setTotal(data.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [levelFilter, requestFilter, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    api.getTTSAnalytics().then(setTtsData).catch(() => {});
  }, []);

  const levelColors: Record<string, string> = {
    error: '#ef4444',
    warn: '#f59e0b',
    info: '#3b82f6',
    debug: '#6b7280',
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const ttsBad = ttsData?.byLanguage?.some(l => parseFloat(l.rate) > 30);

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 4px', color: '#0f172a' }}>Logs</h1>
          <p style={{ color: '#64748b', margin: 0 }}>System logs & TTS analytics</p>
        </div>
        <button
          onClick={() => setShowTTS(!showTTS)}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: `1px solid ${ttsBad ? '#ef4444' : '#e2e8f0'}`,
            background: ttsBad ? '#fef2f2' : '#fff',
            color: ttsBad ? '#ef4444' : '#64748b',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16 }}>{ttsBad ? '⚠' : '📊'}</span>
          TTS Analytics
          {ttsData?.recentFailures ? (
            <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 10,
              padding: '1px 7px', fontSize: 11, fontWeight: 700,
            }}>{ttsData.recentFailures}</span>
          ) : null}
        </button>
      </div>

      {showTTS && ttsData && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#0f172a' }}>TTS Failure Rates (Last 7 Days)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>BY LANGUAGE</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Language</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Total</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Failed</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {ttsData.byLanguage.map(row => (
                    <tr key={row.language} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 500, color: '#0f172a' }}>{row.language}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#64748b' }}>{row.total}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#64748b' }}>{row.failures}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: parseFloat(row.rate) > 30 ? '#ef4444' : parseFloat(row.rate) > 10 ? '#f59e0b' : '#059669' }}>{row.rate}</td>
                    </tr>
                  ))}
                  {ttsData.byLanguage.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>BY PROVIDER</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Provider</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Total</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Failed</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontWeight: 600 }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {ttsData.byProvider.map(row => (
                    <tr key={row.provider} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 500, color: '#0f172a' }}>{row.provider}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#64748b' }}>{row.total}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#64748b' }}>{row.failures}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: parseFloat(row.rate) > 30 ? '#ef4444' : '#059669' }}>{row.rate}</td>
                    </tr>
                  ))}
                  {ttsData.byProvider.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={levelFilter}
          onChange={e => { setLevelFilter(e.target.value); setPage(0); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f172a', background: '#fff' }}
        >
          <option value="">All Levels</option>
          <option value="error">Error</option>
          <option value="warn">Warn</option>
          <option value="info">Info</option>
        </select>
        <input
          type="text"
          placeholder="Filter by request ID..."
          value={requestFilter}
          onChange={e => { setRequestFilter(e.target.value); setPage(0); }}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, flex: 1, minWidth: 200, maxWidth: 400 }}
        />
        <button
          onClick={loadLogs}
          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div>No logs found</div>
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontWeight: 600, width: 80 }}>Level</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontWeight: 600, width: 180 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontWeight: 600 }}>Message</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontWeight: 600, width: 200 }}>Request ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                    <td style={{ padding: '8px 16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: levelColors[log.level] ? `${levelColors[log.level]}15` : '#f1f5f9',
                        color: levelColors[log.level] || '#64748b',
                      }}>{log.level}</span>
                    </td>
                    <td style={{ padding: '8px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 16px', color: '#0f172a', wordBreak: 'break-word' }}>
                      <div style={{ marginBottom: 2 }}>{log.message}</div>
                      {log.metadata && (
                        <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', maxHeight: 80, overflow: 'auto' }}>
                          {log.metadata.length > 200 ? log.metadata.slice(0, 200) + '...' : log.metadata}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>
                      {log.request_id && (
                        <button
                          onClick={() => { setRequestFilter(log.request_id); setPage(0); }}
                          style={{ background: 'none', border: 'none', color: '#0891b2', cursor: 'pointer', fontFamily: 'monospace', fontSize: 11, padding: 0, textDecoration: 'underline' }}
                        >
                          {log.request_id.slice(0, 24)}...
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e2e8f0', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>{total.toLocaleString()} total entries</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
                    background: page === 0 ? '#f1f5f9' : '#fff', color: page === 0 ? '#94a3b8' : '#0f172a',
                    cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  ← Previous
                </button>
                <span style={{ padding: '6px 0', color: '#64748b' }}>Page {page + 1} of {totalPages}</span>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  style={{
                    padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
                    background: page >= totalPages - 1 ? '#f1f5f9' : '#fff', color: page >= totalPages - 1 ? '#94a3b8' : '#0f172a',
                    cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
